import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import MedicationZone from "src/pages/ResultInformation/components/BmiCard/components/MedicationZone";
import WhiteCard from "src/shared/components/WhiteCard";
import { getSeverity } from "../../../AmhReview/utils/getSeverity";
import { useGetPatientScoresQuery } from "src/api/services/mentalHealthApi";

interface Props {
  onNavigate: () => void;
}

const QualificationCard = ({ onNavigate }: Props) => {
  const { t } = useTranslation();
  const component = "amh-plan.qualification-card";

  const { data = { gad7: 0, phq9: 0 } } = useGetPatientScoresQuery();

  const indicatorLabel = useMemo(
    () => t(`${component}.indicator-label`, "You are here"),
    [t]
  );
  const zoneLabel = useMemo(
    () => t(`${component}.zone-label`, "Medication zone"),
    [t]
  );

  const gad7SeverityLabel = useMemo(() => {
    const label = getSeverity("GAD-7", data.gad7);
    if (label == null) return null;

    return `${t(`amh-review.label.${label.toLowerCase()}`, label)} ${t(
      `amh-review.anxiety`,
      "Anxiety"
    )}`;
  }, [data.gad7, t]);

  const phq9SeverityLabel = useMemo(() => {
    const label = getSeverity("PHQ-9", data.phq9);
    if (label == null) return null;

    return `${t(`amh-review.label.${label.toLowerCase()}`, label)} ${t(
      `amh-review.depression`
    )}`;
  }, [data.phq9, t]);

  return (
    <WhiteCard sx={{ background: "#F8F8F8" }} gap={2} onClick={onNavigate}>
      <Typography variant="h6" fontWeight={600}>
        {t(`${component}.header`, "Good news! You qualify for medication")}
      </Typography>
      <Typography>
        {t(
          `${component}.text`,
          "Based on the symptoms you shared, you may qualify for treatment that can help you feel better faster. Your scores give your provider a clear starting point to choose the medication that fits you best."
        )}
      </Typography>
      <Stack gap={2}>
        <Typography>
          {t(
            `${component}.results.header`,
            "Based on your assessments, you scored:"
          )}
        </Typography>
        <Stack gap={5}>
          <Stack spacing={7}>
            <Typography fontWeight={700}>{gad7SeverityLabel}</Typography>
            <MedicationZone
              height={30}
              indicatorLabel={indicatorLabel}
              zoneLabel={zoneLabel}
              value={data.gad7}
              threshold={[0, 5, 10, 16, 21]}
              zoneRange={[5, 21]}
              color="#000000"
            />
          </Stack>
          <Stack spacing={7}>
            <Typography fontWeight={700}>{phq9SeverityLabel}</Typography>
            <MedicationZone
              height={30}
              indicatorLabel={indicatorLabel}
              zoneLabel={zoneLabel}
              value={data.phq9}
              threshold={[0, 6, 13, 20, 27]}
              zoneRange={[6, 27]}
              color="#000000"
              backgroundColor="#236E94"
            />
          </Stack>
        </Stack>
      </Stack>
    </WhiteCard>
  );
};

export default QualificationCard;
